import { put, takeLeading } from 'redux-saga/effects';

import bookingApi from 'api/bookingApi';
import { apiErrorHandler, showSuccess } from 'utils';

import {
  GET_BOOKING_LIST,
  GET_BOOKING_DETAIL,

  APPROVE_BOOKING,
  APPROVE_BOOKING_SUCCESS,
  APPROVE_BOOKING_FAILED,

  REJECT_BOOKING,
  REJECT_BOOKING_SUCCESS,
  REJECT_BOOKING_FAILED,
  EXPORT_BOOKING_LIST,

  REFUND_BOOKING,
  REFUND_BOOKING_SUCCESS,
  REFUND_BOOKING_FAILED,

  CONFIRM_BOOKING,

  OTA_SYNC_BOOKING,
  OTA_SYNC_BOOKING_SUCCESS,
  OTA_SYNC_BOOKING_FAILED,
} from './actionTypes';
import {
  actionGetBookingListSuccess,
  actionGetBookingListFailed,

  actionGetBookingDetailSuccess,
  actionGetBookingDetailFailed,
  actionExportBookingListFailed,
  actionExportBookingListSuccess,

  actionConfirmBookingSuccess,
  actionConfirmBookingFailed,
} from './actions';

function* getBookingList({ payload }) {
  try {
    const response = yield bookingApi.getBookingList(payload);

    yield put(
      actionGetBookingListSuccess(response.payload),
    );
  } catch (error) {
    apiErrorHandler(error);

    yield put(actionGetBookingListFailed());
  }
}

function* exportBookingList({ payload }) {
  try {
    const response = yield bookingApi.exportBookingList(payload);

    window.open(response.payload.downloadUrl, '_blank');

    yield put(actionExportBookingListSuccess());
  } catch (error) {
    apiErrorHandler(error);
    yield put(actionExportBookingListFailed());
  }
}

function* getBookingDetail({ payload }) {
  try {
    const response = yield bookingApi.getBookingDetail(payload);

    yield put(
      actionGetBookingDetailSuccess(response.payload),
    );
  } catch (error) {
    apiErrorHandler(error);

    yield put(actionGetBookingDetailFailed());
  }
}

function* approveBooking({ payload }) {
  try {
    yield bookingApi.approveBooking(payload);

    yield put({
      type: APPROVE_BOOKING_SUCCESS,
    });

    showSuccess('Xác nhận đặt phòng thành công');
  } catch (error) {
    apiErrorHandler(error);

    yield put({
      type: APPROVE_BOOKING_FAILED,
    });
  }
}

export function* cancelBooking({ payload }) {
  try {
    const { callback, onCancelled } = payload;
    const response = yield bookingApi.cancelBooking(payload);

    yield put({
      type: REJECT_BOOKING_SUCCESS,
      payload: response?.payload,
    });

    callback?.();

    // A caller that words its own toast from the refund outcome takes the whole response instead
    // (WP-ADM-CANCEL-REFUND): `refund` sits next to `payload`, not inside it.
    if (onCancelled) onCancelled(response);
    else showSuccess('Xác nhận từ chối đặt phòng thành công');
  } catch (error) {
    apiErrorHandler(error);

    yield put({
      type: REJECT_BOOKING_FAILED,
    });
  }
}

function* refundBooking({ payload }) {
  const { callback, onRefundError, ...body } = payload;
  try {
    yield bookingApi.refundBooking(body);

    yield put({
      type: REFUND_BOOKING_SUCCESS,
    });

    callback?.();
    showSuccess('Hoàn tiền đặt phòng thành công');
  } catch (error) {
    // REFUND_REJECTED / PAYMENT_UNAVAILABLE get their own OnePay-specific toast and follow-up
    // (onepay-refund.md §6) from the page, not the generic handler; every other error (including
    // REFUND_NOT_ONEPAY) shows as today.
    const errorKey = error.response?.data?.key;
    if (onRefundError && ['REFUND_REJECTED', 'PAYMENT_UNAVAILABLE'].includes(errorKey)) {
      onRefundError(error);
    } else {
      apiErrorHandler(error);
    }

    yield put({
      type: REFUND_BOOKING_FAILED,
    });
  }
}

// The endpoint answers only { _id, orderId, otaSync }, never otaBookingId even on SYNCED
// (docs/hotellink/booking-sync.md §4.3), so the caller refetches the full detail on success
// rather than the reducer merging this response's otaSync in. A 200 here only means the retry
// was submitted, not that the sync itself succeeded (an HTTP 200 answer can still carry
// otaSync.status FAILED), so the toast is deliberately neutral.
function* otaSyncBooking({ payload }) {
  try {
    const { callback } = payload;
    yield bookingApi.otaSyncBooking(payload);

    yield put({
      type: OTA_SYNC_BOOKING_SUCCESS,
    });

    callback?.();
    showSuccess('Đã gửi lại yêu cầu đồng bộ Hotellink');
  } catch (error) {
    apiErrorHandler(error);

    yield put({
      type: OTA_SYNC_BOOKING_FAILED,
    });
  }
}

function* confirmBooking({ payload }) {
  try {
    const { callback, id, confirmBooking: confirmCode } = payload || {};
    const response = yield bookingApi.confirmBooking(payload);

    yield put(
      actionConfirmBookingSuccess(response?.payload, {
        bookingId: id,
        confirmBooking: confirmCode,
      }),
    );

    callback?.(response?.payload);
    showSuccess('Xác nhận đặt phòng thành công');
  } catch (error) {
    apiErrorHandler(error);

    yield put(actionConfirmBookingFailed());
  }
}

export default function* bookingSaga() {
  yield takeLeading(GET_BOOKING_LIST, getBookingList);
  yield takeLeading(EXPORT_BOOKING_LIST, exportBookingList);
  yield takeLeading(GET_BOOKING_DETAIL, getBookingDetail);
  yield takeLeading(APPROVE_BOOKING, approveBooking);
  yield takeLeading(CONFIRM_BOOKING, confirmBooking);
  yield takeLeading(REJECT_BOOKING, cancelBooking);
  yield takeLeading(REFUND_BOOKING, refundBooking);
  yield takeLeading(OTA_SYNC_BOOKING, otaSyncBooking);
}
